import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/utils/translations';

// Australia data
const dataAustralia2024 = [
  { institution: "Australian National University (ANU)", count: 3, newVendors: "Career Central (Target Connect); Microsoft Copilot; EchoVideo (Cloud)", themes: "Employability, AI, Cloud Video" },
  { institution: "Curtin University", count: 1, newVendors: "EchoVideo (Cloud)", themes: "Cloud Migration" },
  { institution: "University of Sydney", count: 1, newVendors: "Transact Mobile Credential", themes: "Digital ID / Security" }
];

const dataAustralia2025 = [
  { institution: "Curtin University", count: 5, newVendors: "Respondus LDB; Poll Everywhere; Mentimeter; Learn Ultra; EchoVideo (Cloud rollout)", themes: "Assessment & Engagement Modernization" },
  { institution: "Australian National University (ANU)", count: 3, newVendors: "Inspera; Cadmus; My eQuals Expansion", themes: "Digital Exams & Credentials" },
  { institution: "University of Melbourne (UoM)", count: 2, newVendors: "Cayuse Research Suite; Rimini Street Support", themes: "Research & ERP Transformation" },
  { institution: "Charles Darwin University (CDU)", count: 2, newVendors: "Learn Ultra; Kaltura", themes: "LMS & Video Migration" },
  { institution: "Australian Catholic University (ACU)", count: 2, newVendors: "Canvas; Respondus", themes: "LMS Migration & Assessment" },
  { institution: "Charles Sturt University (CSU)", count: 1, newVendors: "Panopto", themes: "Video Capture" },
  { institution: "Bond University", count: 1, newVendors: "Panopto; Respondus", themes: "Assessment Tools" },
  { institution: "Central Queensland University (CQU)", count: 1, newVendors: "Echo360 Cloud", themes: "Cloud Video" }
];

// Hong Kong data
const dataHongKong2024 = [
  { institution: "City University of Hong Kong (CityU)", count: 3, newVendors: "Ex Libris Primo RA; FeedbackFruits; Okta", themes: "Library AI, Assessment Automation, IAM" },
  { institution: "Hong Kong Baptist University (HKBU)", count: 2, newVendors: "Library & Assessment tools", themes: "Discovery & Automation" },
  { institution: "The Chinese University of Hong Kong (CUHK)", count: 2, newVendors: "Assessment & Collaboration", themes: "T&L Modernization" }
];

const dataHongKong2025 = [
  { institution: "The Hang Seng University of Hong Kong (HSUHK)", count: 2, newVendors: "Turnitin Gradescope/FBS; Library AI", themes: "Assessment Pilots" },
  { institution: "The Education University of Hong Kong (EdUHK)", count: 1, newVendors: "Assessment automation", themes: "T&L Enhancement" },
  { institution: "The University of Hong Kong (HKU)", count: 1, newVendors: "Research information system", themes: "Research Admin" },
  { institution: "Saint Francis University (SFU)", count: 1, newVendors: "Library AI add-on", themes: "Discovery Enhancement" }
];

// United Kingdom data
const dataUK2024 = [
  { institution: "University of Aberdeen", count: 4, newVendors: "Symplicity MyCareerHub; FUTR AI; Fortinet; Virtual Dissection Table", themes: "Careers, AI, Security, STEM Labs" },
  { institution: "University of Edinburgh", count: 4, newVendors: "Questionmark OnDemand; QS Admissions CRM; Wooclap; Symplicity Advocate", themes: "Assessment, Admissions, Engagement, Case Mgmt" },
  { institution: "University of Cumbria", count: 3, newVendors: "EvaSys (Electric Paper); Elsevier Pure; ITSM via Softcat", themes: "Teaching Evaluation, CRIS, ITSM" },
  { institution: "Aston University", count: 2, newVendors: "Explorance Blue; Corporate Project Solutions (Microsoft Portal)", themes: "Course Evaluation, Student Portal" },
  { institution: "Cardiff University", count: 1, newVendors: "Explorance Blue", themes: "Teaching Evaluation" },
  { institution: "University for the Creative Arts (UCA)", count: 1, newVendors: "Panopto", themes: "Lecture Capture" },
  { institution: "University of Dundee", count: 1, newVendors: "Secure Nexus", themes: "Cybersecurity" },
  { institution: "Brunel University London", count: 1, newVendors: "Unibuddy", themes: "Student Engagement" }
];

const dataUK2025 = [
  { institution: "University of Edinburgh", count: 3, newVendors: "Flywire; Kinetic Kx Cloud; WSO2 Choreo", themes: "Payments, Housing, Integration" },
  { institution: "University of Bedfordshire", count: 2, newVendors: "Elastic UK; Phoenix Software Ltd", themes: "Integration, Cybersecurity" },
  { institution: "University for the Creative Arts (UCA)", count: 2, newVendors: "Panopto (licences/software)", themes: "Video CMS" },
  { institution: "Durham University", count: 2, newVendors: "EasyTransfer; FeedbackFruits", themes: "Payments, Peer Learning" },
  { institution: "University of Bristol", count: 1, newVendors: "Panopto (Cloud)", themes: "Lecture Capture" },
  { institution: "University of Aberdeen", count: 1, newVendors: "Vevox", themes: "Classroom Polling" },
  { institution: "Leeds Beckett University", count: 1, newVendors: "Worktribe (Ethics)", themes: "Research Ethics" },
  { institution: "University of West London", count: 1, newVendors: "Explorance Blue + MLY", themes: "Course Evaluation" },
  { institution: "Bishop Grosseteste / Lincoln Bishop", count: 1, newVendors: "EasyTransfer", themes: "Payments" },
  { institution: "Brunel University London", count: 1, newVendors: "Time2Eat", themes: "Campus Dining" }
];

const commentaryAustralia2024 = "Digital modernization & accessibility led by ANU (3 new platforms), followed by Curtin and Sydney focused on cloud and digital ID.";
const commentaryAustralia2025 = "Massive SaaS migration and assessment modernization — Curtin (5 new platforms) and ANU (3) dominated, with Melbourne expanding into research and ERP modernization.";

const commentaryHongKong2024 = "Net-new spend favored bolt-ons over rip-and-replace—libraries added AI at the discovery layer and T&L ran assessment-automation pilots to test impact, while Central IT's only notable net-new was identity hardening (SSO). Translation: low-risk, quick-value buys that deepen existing stacks and create SSO/data footholds for later expansion.";
const commentaryHongKong2025 = "Buying shifted from hygiene to outcome-proof tools—AI became normalized at the library interface and assessment landed via 12-month pilots to build evidence before scaling; research offices modernized with Pure. Still no core system swaps: department-led, modular upgrades that tee up multi-module expansion rather than platform change.";

const commentaryUK2024 = "17 total new vendor adoptions. Most active buyers: Aberdeen and Edinburgh (4 each), Cumbria (3). Top categories: Course & Teaching Evaluation (3), Cybersecurity (2), Student Success/Portal (2). Others include Careers CRM, Chatbot, CRIS, ITSM, Virtual Labs, Admissions CRM, Polling, Assessment (1 each).";
const commentaryUK2025 = "15 total new vendor adoptions. Most active buyers: Edinburgh (3), Bedfordshire (2), UCA (2), Durham (2). Top categories: Payments/Financial Aid (3), Lecture Capture (2), Integration Platforms (2). Remaining one-offs: Polling, Research Ethics, Course Eval, Dining App, Housing, Peer Learning.";

// South Africa data
const dataSouthAfrica2024 = [
  { institution: "Walter Sisulu University (WSU)", count: 4, newVendors: "Invigilator App; Converis; Grammarly; Mimecast + SOC", themes: "Remote Proctoring, CRIS, AI, Cybersecurity" },
  { institution: "University of Pretoria (UP)", count: 2, newVendors: "Anthology Reach; Anthology Milestone + Achievement Studio", themes: "Student Success, Digital Credentials" },
  { institution: "Durban University of Technology (DUT)", count: 2, newVendors: "ITSM (undisclosed); Converis", themes: "ITSM, CRIS" },
  { institution: "University of Johannesburg (UJ)", count: 2, newVendors: "Elsevier Pure; MoUJi AI Chatbot", themes: "CRIS/RIMS, AI Chatbot" },
  { institution: "Stellenbosch University (SU)", count: 2, newVendors: "Explorance Blue; Parchment CeCredentialTrust", themes: "Course Evaluations, Digital Certificates" },
  { institution: "Rhodes University (RU)", count: 2, newVendors: "FeedbackFruits; Flywire", themes: "Peer Learning, International Payments" },
  { institution: "North-West University (NWU)", count: 1, newVendors: "Anthology CRM (Nexus)", themes: "CRM" },
  { institution: "Sefako Makgatho Health Sciences University (SMU)", count: 1, newVendors: "Ex Libris Alma + Primo", themes: "Library System" },
  { institution: "Tshwane University of Technology (TUT)", count: 1, newVendors: "Anthology Engage", themes: "CRM" },
  { institution: "University of the Free State (UFS)", count: 1, newVendors: "Ex Libris Alma + Primo VE", themes: "Library System" },
  { institution: "University of Cape Town – GSB", count: 1, newVendors: "Slate", themes: "Student Recruitment CRM" },
  { institution: "UNISA", count: 1, newVendors: "Flywire", themes: "Student Payments" }
];

const dataSouthAfrica2025 = [
  { institution: "University of Zululand (UNIZULU)", count: 4, newVendors: "Ex Libris Primo; Ex Libris Rapido; Ex Libris Rialto; Primo Research Assistant", themes: "Complete library ecosystem renewal" },
  { institution: "North-West University (NWU)", count: 2, newVendors: "Anthology Student; StarRez", themes: "SIS, Housing" },
  { institution: "Central University of Technology (CUT)", count: 2, newVendors: "Panopto; Adumo", themes: "Video CMS, Student Payments" },
  { institution: "Walter Sisulu University (WSU)", count: 2, newVendors: "EXCALIBAR; Mimecast (expansion)", themes: "Student Debt, Cybersecurity" },
  { institution: "University of Fort Hare (UFH)", count: 1, newVendors: "Figshare", themes: "Research Data" },
  { institution: "Durban University of Technology (DUT)", count: 1, newVendors: "PayGate (DPO)", themes: "Student Payments" },
  { institution: "University of KwaZulu-Natal (UKZN)", count: 1, newVendors: "Flywire", themes: "Student Payments" },
  { institution: "University of Limpopo (UL)", count: 1, newVendors: "Figshare", themes: "Research Data" },
  { institution: "Nelson Mandela University (NMU)", count: 1, newVendors: "Inspera", themes: "e-Assessment/Proctoring" },
  { institution: "Rhodes University (RU)", count: 1, newVendors: "Wooclap", themes: "Classroom Engagement" }
];

const commentarySouthAfrica2024 = "2024 saw the strongest activity from WSU, UP, DUT, UJ, SU, and RU, with a total of 19 confirmed new-vendor purchases across institutions. The year was dominated by upgrades in research management, CRM, library systems, cybersecurity, and digital academic tools.";
const commentarySouthAfrica2025 = "2025 buying was led by UNIZULU, NWU, CUT, and WSU, producing 17 confirmed new-vendor purchases, primarily across library systems, SIS, housing, payments, and research data infrastructure. The strongest vendor winners were Ex Libris, Anthology, Figshare, Flywire, Panopto, and Inspera, reflecting a shift toward full ecosystem replacement and digital service modernisation.";

// Skillwell data (Fortune 500 enterprises)
const dataSkillwell2024 = [
  { institution: "Accenture", count: 4, newVendors: "Workera; Udacity (acquired); Award Solutions (acquired); Stanford Online", themes: "Skills Intelligence, Content M&A, Premium Curriculum" },
  { institution: "S&P Global", count: 1, newVendors: "Accenture LearnVantage", themes: "LXP / Learning Delivery" },
  { institution: "Amazon", count: 2, newVendors: "Fayetteville State University (FSU); Shepherd University", themes: "Career Choice Education Partners" }
];

const dataSkillwell2025 = [
  { institution: "Accenture", count: 5, newVendors: "ChatGPT Enterprise; TalentSprint (acquired); Ascendient Learning (acquired); Aidemy (acquired); SAP Learning", themes: "GenAI Co-pilot, Content M&A, Certification Partners" },
  { institution: "S&P Global", count: 1, newVendors: "Eightfold AI", themes: "Talent Intelligence Platform" },
  { institution: "Amazon", count: 2, newVendors: "University of Southern Indiana (USI); Unmanned Safety Institute", themes: "Career Choice Education & Specialist Partners" }
];

const commentarySkillwell2024 = "2024 shape: One big new LXP relationship (S&P → LearnVantage). One skills-intelligence buy (Accenture → Workera). First content M&A (Udacity, Award Solutions). A steady trickle of new universities into Amazon Career Choice, and one elite specialist curriculum partner (Stanford Online).";
const commentarySkillwell2025 = "2025 shape: No new LXPs, but a clear move up-stack into talent intelligence (Eightfold AI) and GenAI co-pilots (ChatGPT Enterprise) used in the learning context. M&A accelerates: three more content providers pulled into Accenture LearnVantage. Amazon continues to add both universities and specialist providers into Career Choice.";

// Kairadigital data (Manufacturing/Retail/Pharma enterprises)
const dataKairadigital2024 = [
  { institution: "Sanofi", count: 4, newVendors: "Formation Bio; OpenAI; Bluecrux (Axon); ZAGENO", themes: "AI Drug Development, Digital Supply Chain, Lab Procurement" },
  { institution: "L'Oréal Groupe", count: 4, newVendors: "Meta; IMPACT+; ADGREEN; FRUGGR", themes: "Creator/Media, Sustainability Measurement" },
  { institution: "adidas", count: 3, newVendors: "DHL Supply Chain; Kuehne+Nagel; o9 Solutions", themes: "DC Logistics, Retail Transformation" },
  { institution: "H&M Group", count: 3, newVendors: "Rondo Energy; Avassa; Voyado", themes: "Decarbonisation, Edge Platform, CRM" },
  { institution: "Schneider Electric", count: 3, newVendors: "Manhattan Associates; SAP; Microsoft", themes: "WMS, ERP Cloud, Sales Copilot" },
  { institution: "Ingka Group (IKEA)", count: 2, newVendors: "Infosys; Waabi", themes: "Digital Outsourcing, Autonomous Trucking" },
  { institution: "Unilever", count: 2, newVendors: "Oracle; Accenture", themes: "Cloud SCM, GenAI Productivity" },
  { institution: "AstraZeneca", count: 1, newVendors: "OMP", themes: "Global Advanced Planning" },
  { institution: "Volkswagen Group", count: 1, newVendors: "Rivian", themes: "Software JV, EV Platform" },
  { institution: "Nestlé", count: 1, newVendors: "OMP", themes: "Supply Chain Planning" }
];

const dataKairadigital2025 = [
  { institution: "Nestlé", count: 4, newVendors: "Zest; NVIDIA; Accenture Song; Microsoft", themes: "AI Food Waste, Digital Twin Content, Cloud" },
  { institution: "H&M Group", count: 2, newVendors: "Centra; Rebl Industries", themes: "E-commerce, Warehouse Robotics" },
  { institution: "Unilever", count: 2, newVendors: "NVIDIA; Vector Institute", themes: "Digital Twin, AI Research" },
  { institution: "L'Oréal Groupe", count: 2, newVendors: "Google Cloud; NVIDIA", themes: "GenAI Beauty Tech, AI Enterprise" },
  { institution: "AstraZeneca", count: 1, newVendors: "Salesforce", themes: "Agentforce Life Sciences" },
  { institution: "Ingka Group (IKEA)", count: 1, newVendors: "Locus", themes: "Last-Mile Optimisation (acquired)" },
  { institution: "Sanofi", count: 1, newVendors: "QuantHealth", themes: "AI Clinical Digital Twin" },
  { institution: "Schneider Electric", count: 1, newVendors: "Microsoft", themes: "Industrial Copilot" },
  { institution: "Volkswagen Group", count: 1, newVendors: "Dassault Systèmes", themes: "3DEXPERIENCE Cloud Platform" }
];

const commentaryKairadigital2024 = "Heavy concentration in planning and DC/logistics infrastructure, plus early but meaningful moves into AI for R&D (Sanofi) and media/ESG analytics (L'Oréal, H&M).";
const commentaryKairadigital2025 = "Clear swing toward AI + digital twins for content and sustainability, plus industrial / domain-specific copilots (Schneider, clinical for Sanofi, engineering for VW).";

// Plataformaa 2024 data
const dataPlataformaa2024 = [
  { institution: "UNAM", count: 1, newVendors: "Adobe Creative Cloud (via Definición Total)", themes: "Content Creation / Creative Suite" },
  { institution: "Universidad de La Frontera (UFRO)", count: 1, newVendors: "Adobe Premiere Pro for Teams (via FLEX SSS SpA)", themes: "Content Creation / Video Production" },
  { institution: "Universidad Distrital FJC", count: 1, newVendors: "Unity simulation platform", themes: "Simulation / Immersive Labs (AR/VR)" },
  { institution: "Universidad de Guadalajara (UdeG)", count: 1, newVendors: "Coursera Career Academy / Skills Hub (250 licenses)", themes: "Workforce Skills / Micro-credentials" },
  { institution: "UNAD", count: 1, newVendors: "Plataforma de Evaluación Online Ilimitada (3-year)", themes: "Online Assessment" }
];

// Plataformaa 2025 data (pipeline/usage, not awarded)
const dataPlataformaa2025 = [
  { institution: "UNAL", count: 1, newVendors: "eInnovation cloud platform (pipeline)", themes: "Cloud Access + Virtual Assistant" }
];

const commentaryPlataformaa2024 = "2024 buying was led by content creation tools (Adobe at UNAM and UFRO), immersive simulation (Unity at Universidad Distrital), workforce skills platforms (Coursera at UdeG), and online assessment infrastructure (UNAD's 3-year platform license). Total: 5 confirmed new vendor deployments across diverse categories.";
const commentaryPlataformaa2025 = "2025 shows minimal confirmed new vendor wins. UNAL has an eInnovation cloud platform + virtual assistant in pipeline (not yet awarded). The year appears focused on adoption and scaling of 2024 investments rather than major new procurements.";

// Wayground 2024 data
const dataWayground2024 = [
  { institution: "Jalisco – Secretaría de Educación", count: 6, newVendors: "Multiple vendors for laptops, mobile carts, displays, interactive screens, STEM/robotics kits", themes: "Devices & Classroom Hardware, STEM/Robotics" },
  { institution: "Nuevo León – Secretaría de Educación", count: 5, newVendors: "Softya, Inter @ Enter, Telmex, Edutest, BEO", themes: "Devices, Connectivity, Assessment, Teacher Development" },
  { institution: "Baja California – Secretaría de Educación", count: 1, newVendors: "Google Workspace for Education", themes: "Learning & Collaboration Platforms" },
  { institution: "UGEL 03 – Lima Metropolitana", count: 1, newVendors: "Storage solution vendor", themes: "Connectivity & Core Infrastructure" },
  { institution: "Chihuahua – SEECh", count: 1, newVendors: "Computer hardware vendor", themes: "Devices & Classroom Hardware" }
];

// Wayground 2025 data
const dataWayground2025 = [
  { institution: "Plan Ceibal / ANEP", count: 4, newVendors: "Laptop vendors, Tablet OEMs, SaaS platform providers for national education and data management", themes: "Devices, National Platform, Data Infrastructure" },
  { institution: "Jalisco – Secretaría de Educación", count: 1, newVendors: "Bundled hardware + software + implementation vendor", themes: "Devices & Classroom Software" },
  { institution: "Nuevo León – Secretaría de Educación", count: 1, newVendors: "ITEP México", themes: "Assessment & Certification" },
  { institution: "Chihuahua – SEECh", count: 1, newVendors: "Computer equipment vendor", themes: "Devices & Classroom Hardware" },
  { institution: "SLEP Atacama", count: 1, newVendors: "Diversi SpA", themes: "Admin & Support Platforms" }
];

const commentaryWayground2024 = "2024 saw Jalisco and Nuevo León as the most active buyers, with large-scale device procurements and strategic investments in connectivity and assessment. Jalisco led with 6 separate hardware and STEM deployments, while Nuevo León diversified across 5 categories including teacher certification.";
const commentaryWayground2025 = "2025 shows a shift toward system-level plays: Plan Ceibal/ANEP dominates with 4 major infrastructure investments including national platforms and devices. Nuevo León continues its assessment focus with ITEP diagnostic licenses, while SLEP Atacama invests in special-education management.";

// Learnwise data (global higher education – academic integrity / originality focus)
const dataLearnwise2024 = [
  { institution: "Mapúa University", count: 2, newVendors: "Turnitin + Turnitin Draft Coach", themes: "Strongest 2024 multi-product rollout within one vendor family" },
  { institution: "University of Technology Sydney", count: 1, newVendors: "CourseLoop", themes: "Clear 2024 move into curriculum-management system" },
  { institution: "University of the Republic (Uruguay)", count: 1, newVendors: "Compilatio", themes: "Academic integrity" },
  { institution: "Universiti Malaya", count: 1, newVendors: "Alibaba Cloud Academic Empowerment + Academy Skills Centre", themes: "New Alibaba Cloud skills program" },
  { institution: "Federal University of Espírito Santo (UFES)", count: 1, newVendors: "Turnitin Similarity", themes: "Shows up in both years" },
  { institution: "Miriam College", count: 1, newVendors: "Canvas LMS", themes: "LMS migration" }
];

const dataLearnwise2025 = [
  { institution: "Federal University of Espírito Santo (UFES)", count: 1, newVendors: "Turnitin AI Writing module", themes: "Shows up in both years: Similarity in 2024, AI Writing in 2025" },
  { institution: "Federal University of Bahia (UFBA)", count: 1, newVendors: "Turnitin Similarity Originality Add-On", themes: "Academic integrity" },
  { institution: "Universidad Central de Chile (UCEN)", count: 1, newVendors: "Clarivate / IDEA UCEN", themes: "Clear 2025 platform replacement with new named vendor" },
  { institution: "Universidad Autónoma de San Luis Potosí (UASLP)", count: 1, newVendors: "iThenticate", themes: "Academic integrity" },
  { institution: "Federal University of Uberlândia (UFU)", count: 1, newVendors: "Plagius", themes: "Plagiarism detection" },
  { institution: "Universidad Técnica Federico Santa María (UTFSM)", count: 1, newVendors: "Compilatio", themes: "Clear 2025 new Compilatio implementation" }
];

const commentaryLearnwise2024 = "Mapúa led with the strongest multi-product rollout (Turnitin + Draft Coach). UTS stood out with a curriculum-management purchase (CourseLoop). University of the Republic and UFES adopted integrity tools; Universiti Malaya launched Alibaba Cloud skills programs; Miriam College moved to Canvas.";
const commentaryLearnwise2025 = "UFES appears in both years—Turnitin Similarity in 2024, then Turnitin AI Writing module in 2025. UCEN introduced a new Clarivate-powered discovery platform. UTFSM and UASLP added Compilatio and iThenticate respectively. UFU and UFBA rounded out the integrity-focused buying. The market remains concentrated in academic integrity, with selective moves into curriculum management and LMS.";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-[#0A1A2F] text-white p-3 rounded-lg shadow-lg max-w-md border border-[#00C2C2]">
        <p className="font-semibold text-sm mb-1">{data.institution}</p>
        <p className="text-xs text-[#00C2C2] mb-2">{data.count} New Vendor Deployments</p>
        <p className="text-xs mb-1"><span className="font-semibold">New Vendors:</span> {data.newVendors}</p>
        <p className="text-xs"><span className="font-semibold">Key Themes:</span> {data.themes}</p>
      </div>
    );
  }
  return null;
};

export default function TopBuyers({ market }) {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState('2025');
  
  const isHongKong = market === 'hong-kong';
  const isUK = market === 'united-kingdom';
  const isSouthAfrica = market === 'south-africa';
  const isSkillwell = market === 'skillwell';
  const isKairadigital = market === 'kairadigital';
  const isWayground = market === 'wayground';
  const isPlataformaa = market === 'plataformaa';
  const isLearnwise = market === 'learnwise';
  
  let data2024, data2025, commentary2024, commentary2025;
  
  if (isLearnwise) {
    data2024 = dataLearnwise2024;
    data2025 = dataLearnwise2025;
    commentary2024 = commentaryLearnwise2024;
    commentary2025 = commentaryLearnwise2025;
  } else if (isPlataformaa) {
    data2024 = dataPlataformaa2024;
    data2025 = dataPlataformaa2025;
    commentary2024 = commentaryPlataformaa2024;
    commentary2025 = commentaryPlataformaa2025;
  } else if (isWayground) {
    data2024 = dataWayground2024;
    data2025 = dataWayground2025;
    commentary2024 = commentaryWayground2024;
    commentary2025 = commentaryWayground2025;
  } else if (isKairadigital) {
    data2024 = dataKairadigital2024;
    data2025 = dataKairadigital2025;
    commentary2024 = commentaryKairadigital2024;
    commentary2025 = commentaryKairadigital2025;
  } else if (isSkillwell) {
    data2024 = dataSkillwell2024;
    data2025 = dataSkillwell2025;
    commentary2024 = commentarySkillwell2024;
    commentary2025 = commentarySkillwell2025;
  } else if (isUK) {
    data2024 = dataUK2024;
    data2025 = dataUK2025;
    commentary2024 = commentaryUK2024;
    commentary2025 = commentaryUK2025;
  } else if (isHongKong) {
    data2024 = dataHongKong2024;
    data2025 = dataHongKong2025;
    commentary2024 = commentaryHongKong2024;
    commentary2025 = commentaryHongKong2025;
  } else if (isSouthAfrica) {
    data2024 = dataSouthAfrica2024;
    data2025 = dataSouthAfrica2025;
    commentary2024 = commentarySouthAfrica2024;
    commentary2025 = commentarySouthAfrica2025;
  } else {
    // Default to Australia if market is not Hong Kong, UK, South Africa, or Skillwell
    data2024 = dataAustralia2024;
    data2025 = dataAustralia2025;
    commentary2024 = commentaryAustralia2024;
    commentary2025 = commentaryAustralia2025;
  }
  
  const data = selectedYear === '2025' ? data2025 : data2024;
  const sortedData = [...data].sort((a, b) => b.count - a.count);

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('topBuyers')}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={selectedYear === '2024' ? 'default' : 'outline'}
              onClick={() => setSelectedYear('2024')}
              className={selectedYear === '2024' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2024
            </Button>
            <Button
              size="sm"
              variant={selectedYear === '2025' ? 'default' : 'outline'}
              onClick={() => setSelectedYear('2025')}
              className={selectedYear === '2025' ? 'bg-[#00C2C2] hover:bg-[#00A3A3]' : ''}
            >
              2025
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#5C6B7A]">{(isSkillwell || isKairadigital) ? t('enterprisesMostVendors') : t('universitiesMostVendors')}</p>
        <p className="text-xs text-[#5C6B7A] mt-1">Illustrative sample — not live market intelligence for this tenant.</p>
        {isUK && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">(data is based on 16 non-Canvas universities)</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={sortedData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.2} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#5C6B7A" />
              <YAxis 
                type="category" 
                dataKey="institution" 
                tick={{ fontSize: 10 }}
                stroke="#5C6B7A"
                width={200}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 194, 194, 0.1)' }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {sortedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={index < 3 ? '#00C2C2' : '#89CDD4'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-6 md:mt-8 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-[#0A1A2F] mb-1">{t('headlineTrends')}</p>
          <p className="text-xs text-[#5C6B7A] leading-relaxed">
            {selectedYear === '2024' ? commentary2024 : commentary2025}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}